import { defineComponent as _defineComponent } from 'vue'
import { PositionedInventory } from '@/models/Inventory'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import Activity from '@/models/activity'
import { NormalizedInventory } from '@/store/modules/assets'
import { cloneDeep, groupBy, orderBy } from 'lodash'
import NewLocationPicker from '@/components/locations/NewLocationPicker.vue'
import JobInventoryGroupEdit from '@/components/tickets/JobInventoryGroupEdit.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import VsForm from '@/components/vision/VsForm.vue'
import { JobStatus } from '@/models/job'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'


export default /*#__PURE__*/_defineComponent({
  __name: 'JobLocationsAndInventoryEdit',
  props: {
    jobHubId: null,
    activityId: { default: null },
    destinationLocationId: { default: null },
    inventories: null,
    isPickupRequired: { type: Boolean },
    isDisabled: { type: Boolean, default: false }
  },
  emits: ['update-inventories', 'update-destination'],
  setup(__props: any, { expose, emit }) {

const props = __props as { jobHubId: string, activityId: number | null, destinationLocationId: number | null, inventories: PositionedInventory[], isPickupRequired: boolean, isDisabled: boolean }





const inventoryEditForm = ref()
const errorMessagesArray = ref<string[][]>([])
const store = useStore()
const { canShowAllLocationsInLocationPicker, canCreateLocationOnTheFly } =
  useBusinessUnitPermissions()
const availableInventoriesByResourceTypeActivity = computed<
  (resourceTypeActivityId?: number) => NormalizedInventory[]
>(() => store.getters['assets/getInventoriesByResourceTypeActivity'])
const getActivityById = computed<(id?: number | null) => Activity>(
  () => store.getters['activities/getById']
)
const availableInventories = computed(() =>
  availableInventoriesByResourceTypeActivity.value(
    props.activityId ?? undefined
  )
)
const keyedInventories = computed(() =>
  props.inventories.map((item, index) => ({
    ...item,
    InvKey: `${item.inventorySubTypeId}_${item.pickupLocationId}_${item.position}_${index}`,
  }))
)
const inventoryGroupedAndSortedByPosition = computed(() => {
  const groupedInventories = groupBy(keyedInventories.value, (i) => i.position)
  return orderBy(
    Object.keys(groupedInventories).map((position) => {
      const inventories = groupedInventories[position]

      return {
        pickupLocationId: inventories[0].pickupLocationId,
        position: parseInt(position),
        inventories,
      }
    }),
    'position'
  )
})
const hasInventories = computed(() => !!keyedInventories.value.length)

function addPickupLocation(position: number) {
  errorMessagesArray.value = []
  const newInventories = cloneDeep(keyedInventories.value)

  for (const inventory of newInventories.filter(
    (i) => i.position >= position
  )) {
    inventory.position++
  }

  addInventory(position, undefined)
}

function updatePickupLocation(
  newPickupLocationId: number | null | undefined,
  position: number
) {
  errorMessagesArray.value = []
  const newInventories = keyedInventories.value.map((i) => {
    if (i.position === position) {
      return { ...i, pickupLocationId: newPickupLocationId }
    } else {
      return i
    }
  })
  emit('update-inventories', newInventories)
}

function removePickupLocation(position: number) {
  errorMessagesArray.value = []
  const newInventories = cloneDeep(
    keyedInventories.value.filter((i) => i.position !== position)
  )

  for (const inventory of newInventories.filter((i) => i.position > position)) {
    inventory.position--
  }

  emit('update-inventories', newInventories)
}

function addInventory(position: number, pickupLocationId?: number | null) {
  errorMessagesArray.value = []
  const canUseSuggestedItem =
    !keyedInventories.value.length &&
    availableInventories.value.length === 1 &&
    availableInventories.value[0].assetConditions.length === 1

  const suggestedItem = canUseSuggestedItem
    ? availableInventories.value[0]
    : {
        id: null,
        name: null,
        assetConditions: null,
      }

  const newItem = {
    id: suggestedItem.id ?? null,
    name: suggestedItem.name ?? null,
    inventoryTypeId: suggestedItem.id ?? null,
    inventoryTypeName: suggestedItem.name ?? null,
    inventorySubTypeId: suggestedItem.assetConditions?.[0].id ?? null,
    inventorySubTypeName: suggestedItem.assetConditions?.[0].name ?? null,
    inventoryId: suggestedItem.id ?? null,
    pickupLocationId,
    position,
  }

  emit('update-inventories', [...keyedInventories.value, newItem])
}

function updateInventory(
  invKey: string,
  newInventory: PositionedInventory & { InvKey: string }
) {
  errorMessagesArray.value = []
  const newInventories = [...keyedInventories.value]
  const inventoryIndex = newInventories.findIndex((i) => i.InvKey === invKey)
  newInventories.splice(inventoryIndex, 1, newInventory)
  emit('update-inventories', newInventories)
}

function removeInventory(invKey: string) {
  errorMessagesArray.value = []
  const inventoryIndex = keyedInventories.value.findIndex(
    (i) => i.InvKey === invKey
  )
  const position = keyedInventories.value[inventoryIndex].position
  if (
    keyedInventories.value.filter((i) => i.position === position).length > 1
  ) {
    const newInventories = [...keyedInventories.value]
    newInventories.splice(inventoryIndex, 1)
    emit('update-inventories', newInventories)
  } else {
    removePickupLocation(position)
  }
}

function clearAnyIncompatibleInventories() {
  const newInventories = props.inventories.map((i) => {
    const blankInventoryItem = {
      ...i,
      id: null,
      name: null,
      inventoryTypeId: null,
      inventoryTypeName: null,
      inventorySubTypeId: null,
      inventorySubTypeName: null,
    }

    const inventoryItem = availableInventories.value.find(
      (si) => si.id === i.inventoryTypeId
    )
    if (!inventoryItem) {
      return blankInventoryItem
    }

    const inventorySubType = inventoryItem.assetConditions.find(
      (ac) => ac.id === i.inventorySubTypeId
    )
    if (!inventorySubType) {
      return blankInventoryItem
    }

    return i
  })

  emit('update-inventories', newInventories)
}

function validate(jobStatus: JobStatus) {
  const requiredFieldsFilled = inventoryEditForm.value.isValid()

  let result = true
  for (const invGroup of inventoryGroupedAndSortedByPosition.value) {
    let showErrorMessageForThisInventoryGroup = false
    for (const inv1 of invGroup.inventories) {
      let numOccurence = 0
      for (const inv2 of invGroup.inventories) {
        if (inv2.inventorySubTypeId === inv1.inventorySubTypeId) {
          numOccurence++
        }
      }
      if (numOccurence > 1) {
        showErrorMessageForThisInventoryGroup = true
        result &&= false
        break
      }
    }
    if (showErrorMessageForThisInventoryGroup) {
      errorMessagesArray.value.push([
        'Duplicated inventory items in the same location. Please choose a different item.',
      ])
    } else {
      errorMessagesArray.value.push([])
    }
  }
  if (!result) return false
  errorMessagesArray.value = []

  if (jobStatus === JobStatus.COMPLETED) {
    const emptyLocationGroups =
      inventoryGroupedAndSortedByPosition.value.filter(
        (g) => !g.pickupLocationId
      )

    for (const group of inventoryGroupedAndSortedByPosition.value) {
      if (!group.pickupLocationId) {
        errorMessagesArray.value.push([
          `Pickup location must be chosen to ${
            props.isPickupRequired ? 'save' : 'complete'
          }`,
        ])
      } else {
        errorMessagesArray.value.push([])
      }
    }
    result = requiredFieldsFilled && !emptyLocationGroups.length
  } else {
    result = requiredFieldsFilled
  }

  if (result) {
    errorMessagesArray.value = []
  }
  return result
}

watch(
  () => props.activityId,
  function () {
    const hasAvailableInventoryButNoneSelected =
      keyedInventories.value.length === 0 &&
      availableInventories.value.length > 0

    const activity = getActivityById.value(props.activityId)
    const inventoryAssociations = activity?.inventoryAssociations || []

    if (
      hasAvailableInventoryButNoneSelected &&
      inventoryAssociations.length > 0
    ) {
      addInventory(0, undefined)
    }
  }
)
watch(availableInventories, function () {
  clearAnyIncompatibleInventories()
})

expose({ validate })

return { __sfc: true,props, emit, inventoryEditForm, errorMessagesArray, store, canShowAllLocationsInLocationPicker, canCreateLocationOnTheFly, availableInventoriesByResourceTypeActivity, getActivityById, availableInventories, keyedInventories, inventoryGroupedAndSortedByPosition, hasInventories, addPickupLocation, updatePickupLocation, removePickupLocation, addInventory, updateInventory, removeInventory, clearAnyIncompatibleInventories, validate, NewLocationPicker, JobInventoryGroupEdit, VsButton, VsText, VsForm }
}

})